import { ViewBookingStatusEnum } from "collins-js-sdk";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import SelectedBookingHeader from "./Header/SelectedBookingHeader";

import { Hand, X } from "lucide-react";
import { errorHandler } from "../../../../APIs/errorHandler";
import { useBookingPermissions } from "../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../hooks/useDiaryContext";
import { useUserContext } from "../../../../hooks/useUserContext";
import { takeEnquiryRequest } from "../../../../pages/enquiries/api";
import { EnquiryStatus } from "../../../../types/enquiries";
import { EDSButton } from "../../../eds/EDSButton";
import { Spinner } from "../../../ui/spinner";
import EditBooking from "./EditBooking/EditBooking";
import { EditBookingArea } from "./EditBooking/EditBookingArea";
import SelectedBookingLabels from "./Labels/SelectedBookingLabels";
import SelectedBookingTabs from "./Tabs/SelectedBookingTabs";

const mapEnquiryStatusToBookingStatus = (
  status: EnquiryStatus
): ViewBookingStatusEnum => {
  switch (status) {
    case "new":
      return ViewBookingStatusEnum.New;
    case "in_progress":
      return ViewBookingStatusEnum.InProgress;
    case "complete":
      return ViewBookingStatusEnum.Complete;
    case "cancelled":
      return ViewBookingStatusEnum.Rejected;
    default:
      return ViewBookingStatusEnum.New;
  }
};

const SelectedBooking = () => {
  const {
    selectedBooking,
    setDiaryContext,
    editingFromPreviewModal,
    openedFromEnquiry,
    openedFromSpotlight,
    selectedEnquiry,
    enquiriesReturnSearch
  } = useDiaryContext();
  const { user } = useUserContext();
  const { userCanManageBooking } = useBookingPermissions();
  const navigate = useNavigate();
  const [showEditBooking, setShowEditBooking] = useState<boolean>(false);
  const [showEditArea, setShowEditArea] = useState<boolean>(false);
  const [isTaking, setIsTaking] = useState<boolean>(false);

  const showTakeButton = openedFromEnquiry && selectedEnquiry?.status === "new";

  const handleTakeEnquiry = useCallback(async () => {
    if (!selectedEnquiry?._id || !user?._id) return;

    setIsTaking(true);
    try {
      const updatedEnquiry = await takeEnquiryRequest(
        selectedEnquiry._id,
        user._id
      );
      const mergedEnquiry = { ...selectedEnquiry, ...updatedEnquiry };
      setDiaryContext({
        selectedEnquiry: mergedEnquiry,
        selectedBooking: selectedBooking
          ? {
              ...selectedBooking,
              status: mapEnquiryStatusToBookingStatus(mergedEnquiry.status)
            }
          : undefined
      });
    } catch (error) {
      await errorHandler(error, "Failed to take enquiry");
    } finally {
      setIsTaking(false);
    }
  }, [selectedEnquiry, selectedBooking, user?._id, setDiaryContext]);

  const handleClose = useCallback(() => {
    // Navigate FIRST so location.pathname leaves /bookings/:id before
    // useUpdateStateFromURL sees the cleared selectedBookingId — otherwise
    // the URL→context sync re-populates the selection and the detail
    // flashes closed then re-opens.
    const returnSearch = enquiriesReturnSearch ?? "";
    let destination = "/diary";
    if (openedFromEnquiry && !openedFromSpotlight) {
      destination = `/enquiries${returnSearch}`;
    }
    navigate(destination);
    setDiaryContext({
      selectedBooking: undefined,
      selectedBookingV4: undefined,
      selectedBookingId: undefined,
      openedFromEnquiry: false,
      openedFromSpotlight: false,
      selectedEnquiry: undefined,
      enquiriesReturnSearch: undefined
    });
  }, [
    setDiaryContext,
    navigate,
    openedFromEnquiry,
    openedFromSpotlight,
    enquiriesReturnSearch
  ]);

  if (!selectedBooking) {
    return (
      <div className="EditPanel flex flex-col flex-[1_1_100%] h-[calc(100dvh-241px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] min-h-[calc(100dvh-241px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] bg-[var(--diaryGridSolid)] overflow-hidden max-[480px]:absolute max-[480px]:left-0 max-[480px]:right-0 max-[480px]:z-[15] max-[480px]:h-[calc(100dvh-60px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] max-[480px]:min-h-[calc(100dvh-60px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] items-center justify-center">
        <Spinner className="h-12 w-12" />
      </div>
    );
  }
  const handleEditClose = () => {
    setShowEditBooking(false);

    if (editingFromPreviewModal) {
      if (selectedBooking !== undefined) {
        setDiaryContext({
          editingFromPreviewModal: false,
          selectedView: "RunsheetTab",
          selectedBooking: undefined,
          selectedBookingV4: undefined
        });
      } else {
        setDiaryContext({ editingFromPreviewModal: false });
      }
    } else {
      setDiaryContext({ editingFromPreviewModal: false });
    }
  };

  return (
    <div className="EditPanel flex flex-col flex-[1_1_100%] h-[calc(100dvh-241px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] min-h-[calc(100dvh-241px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] bg-[var(--diaryGridSolid)] overflow-hidden max-[480px]:absolute max-[480px]:left-0 max-[480px]:right-0 max-[480px]:z-[15] max-[480px]:h-[calc(100dvh-60px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] max-[480px]:min-h-[calc(100dvh-60px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))]">
      {(showEditBooking || editingFromPreviewModal) && (
        <EditBooking handleClose={handleEditClose} />
      )}
      {showEditArea && (
        <EditBookingArea handleClose={() => setShowEditArea(false)} />
      )}
      <div className="HeaderPanel m-4 absolute right-0">
        <div className="Buttons flex gap-3">
          {showTakeButton && (
            <EDSButton
              variant="default"
              size="md"
              className="EditButton w-[125px]"
              onClick={handleTakeEnquiry}
              disabled={isTaking}
            >
              <Hand className="h-4 w-4 mr-2" />
              {isTaking ? "Taking..." : "Take"}
            </EDSButton>
          )}
          {userCanManageBooking && (
            <EDSButton
              variant="outline"
              size="md"
              className="EditButton w-[125px]"
              onClick={() => setShowEditArea(true)}
            >
              Change Area
            </EDSButton>
          )}
          {userCanManageBooking && (
            <EDSButton
              variant="outline"
              size="md"
              className="EditButton w-[125px]"
              onClick={() => setShowEditBooking(true)}
            >
              Edit
            </EDSButton>
          )}
          <EDSButton
            variant="outline"
            size="icon"
            className="closeButton"
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </EDSButton>
        </div>
      </div>

      <SelectedBookingHeader />
      <SelectedBookingLabels />
      <SelectedBookingTabs />
    </div>
  );
};

export default SelectedBooking;
